import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  FolderArrowDownIcon,
  FolderPlusIcon,
  InformationCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  type FolderDeleteBlocker,
  type FolderDeletePreview,
  type FolderImpact,
  type FolderOpResult,
  folderNameOf,
  isSelfOrDescendantFolder,
  parentFolderPath,
  sanitizeFolderName,
} from '@xphoto/shared';
import { ModalOverlay } from '../components/ModalOverlay';
import { confirmWordMatches } from '../components/confirm-word';
import { useModalA11y } from '../components/useModalA11y';
import { ApiError } from '../gallery/api';
import { interpolate } from '../gallery/format';
import { useI18n, type MessageKey } from '../i18n';
import { FolderTreePicker, type FolderTreePickerLabels } from './FolderTreePicker';
import { buildFolderTree, type FolderNode } from './folder-tree';
import {
  createFolder,
  deleteFolder,
  fetchFolderDeletePreview,
  fetchFolderImpact,
  moveFolder,
  renameFolder,
} from './folder-ops-api';
import { useAllFolders } from './use-all-folders';

/**
 * Fenêtre des ÉCRITURES SUR LES DOSSIERS (XPHOTO-32, lots 4 et 5) : créer, renommer,
 * déplacer, supprimer.
 *
 * UN SEUL composant pour les quatre gestes, et c'est délibéré : ils partagent la coquille
 * de dialogue, le verrou pendant l'appel, la table des erreurs et — pour ceux qui portent
 * sur un dossier existant — la lecture préalable qui alimente la confirmation. Quatre
 * composants auraient recopié tout cela, et l'auraient laissé diverger au premier
 * correctif. Seul le CORPS change avec l'opération.
 *
 * CE QUE LA CONFIRMATION DOIT DIRE :
 *  - renommer / déplacer : ce qui SUIVRA tout seul (médias, partages publics `/p/:token`,
 *    accès par sous-arbre, propagés dans la même transaction) et ce qui NE SUIVRA PAS
 *    (les FILTRES ENREGISTRÉS au prédicat `dossier:`, volontairement pas réécrits — cf.
 *    l'en-tête de `packages/shared/src/folder-ops.ts`) ;
 *  - supprimer : ce qui va DISPARAÎTRE (combien de répertoires, quels fichiers système)
 *    ou POURQUOI ce sera refusé, AVANT de proposer le bouton. Un aperçu périmé ne décide
 *    de rien : le serveur re-calcule tout au moment d'agir.
 *
 * L'appel est SYNCHRONE (pas de job, pas de cloche) : au retour, l'arborescence a déjà
 * changé. L'appelant invalide donc ses caches et, s'il était DANS le dossier touché,
 * navigue vers le chemin que `onDone` lui remet (le nouveau, ou le parent après une
 * suppression).
 */

/**
 * Le geste demandé. `create` porte sur le dossier PARENT, les trois autres sur le
 * dossier lui-même.
 */
export type FolderOpKind = 'create' | 'rename' | 'move' | 'delete';

export interface FolderOpTarget {
  kind: FolderOpKind;
  /**
   * Pour `create` : le dossier HÔTE (`''` = racine). Pour `rename`/`move`/`delete` : le
   * dossier VISÉ lui-même (jamais `''` — la racine ne se renomme, ne se déplace ni ne se
   * supprime).
   */
  path: string;
}

interface FolderOpModalProps {
  libraryId: string;
  target: FolderOpTarget;
  onClose: () => void;
  /** Appelé après un succès, avec le résultat (dont le chemin FINAL du dossier). */
  onDone: (result: FolderOpResult, kind: FolderOpKind) => void;
}

/** Erreurs distinguées à l'écran (chacune appelle une conduite différente). */
type OpError =
  | 'writeDisabled'
  | 'conflict'
  | 'crossDevice'
  | 'outOfSync'
  | 'notEmpty'
  | 'hasAssets'
  | 'invalid'
  | 'generic';

/**
 * Message affiché par cas d'erreur. Table de clés LITTÉRALES plutôt qu'une clé construite
 * par gabarit : le projet réserve les préfixes dynamiques à quelques endroits recensés, et
 * la garde `i18n-unused-keys.test.ts` veut qu'en ajouter un reste un geste conscient. Une
 * table typée `Record<…, MessageKey>` est le patron courant ici (cf. `ROLE_LABEL`).
 */
const ERROR_MESSAGE_KEY: Record<OpError, MessageKey> = {
  writeDisabled: 'folderOps.errorWriteDisabled',
  conflict: 'folderOps.errorConflict',
  crossDevice: 'folderOps.errorCrossDevice',
  outOfSync: 'folderOps.errorOutOfSync',
  notEmpty: 'folderOps.errorNotEmpty',
  hasAssets: 'folderOps.errorHasAssets',
  invalid: 'folderOps.errorInvalid',
  generic: 'folderOps.errorGeneric',
};

/**
 * Traduit l'échec d'appel en cas d'erreur affichable.
 *
 * Les deux refus de SUPPRESSION passent AVANT le 409 générique : « le dossier n'est pas
 * vide » et « des médias y sont encore indexés » sont deux conduites distinctes, et
 * aucune des deux n'est « un dossier porte déjà ce nom ».
 */
function classifyError(err: unknown): OpError {
  if (!(err instanceof ApiError)) return 'generic';
  if (err.code === 'WRITE_DISABLED') return 'writeDisabled';
  if (err.code === 'CROSS_DEVICE') return 'crossDevice';
  if (err.code === 'DB_OUT_OF_SYNC') return 'outOfSync';
  if (err.code === 'FOLDER_NOT_EMPTY') return 'notEmpty';
  if (err.code === 'FOLDER_HAS_ASSETS') return 'hasAssets';
  if (err.status === 409) return 'conflict';
  if (err.status === 400) return 'invalid';
  if (err.status === 404) return 'conflict';
  return 'generic';
}

/**
 * Motif de refus → phrase affichée. Chaque motif a SA phrase : « videz le dossier » et
 * « nettoyez l'index » ne s'obtiennent pas au même endroit, et les fondre en un message
 * générique renverrait l'utilisateur au mauvais écran.
 *
 * `indexed_assets` n'y figure PAS : son message porte un COMPTE, donc il se pluralise
 * (`tp`) et sa clé n'est pas une `MessageKey` mais une base plurielle.
 */
const BLOCKER_MESSAGE_KEY: Record<Exclude<FolderDeleteBlocker, 'indexed_assets'>, MessageKey> = {
  other_files: 'folderOps.blockerOtherFiles',
  hidden_dir: 'folderOps.blockerHiddenDir',
  unreadable: 'folderOps.blockerUnreadable',
};

export function FolderOpModal({
  libraryId,
  target,
  onClose,
  onDone,
}: FolderOpModalProps): React.JSX.Element {
  const { t, tp } = useI18n();
  const closeRef = useRef<HTMLButtonElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const isCreate = target.kind === 'create';
  const isMove = target.kind === 'move';
  const isDelete = target.kind === 'delete';

  // Nom saisi : vide à la création, PRÉ-REMPLI avec le nom courant au renommage (on
  // corrige presque toujours un nom existant, on ne le retape pas).
  const [name, setName] = useState(() => (isCreate ? '' : folderNameOf(target.path)));
  // Destination du déplacement : le parent COURANT par défaut, pour que l'arbre s'ouvre
  // là où le dossier vit aujourd'hui plutôt qu'à la racine.
  const [destParent, setDestParent] = useState(() => (isMove ? parentFolderPath(target.path) : ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<OpError | null>(null);
  const [impact, setImpact] = useState<FolderImpact | null>(null);
  // Aperçu de SUPPRESSION : `undefined` = pas encore répondu, `null` = indisponible.
  const [preview, setPreview] = useState<FolderDeletePreview | null | undefined>(undefined);
  // Effacer aussi les fichiers système : opt-in EXPLICITE, jamais coché par défaut —
  // c'est le seul chemin du produit où un fichier est supprimé (ADR-0010 amendée).
  const [removeSystemFiles, setRemoveSystemFiles] = useState(false);
  // Mot de confirmation recopié (suppression uniquement).
  const [typed, setTyped] = useState('');

  useModalA11y({
    dialogRef,
    initialFocusRef: isMove || isDelete ? closeRef : nameInputRef,
    onEscape: !busy ? onClose : undefined,
  });

  // IMPACT : uniquement pour les deux gestes qui changent un chemin EXISTANT. Un échec
  // est AVALÉ (l'avertissement reste masqué) — une confirmation ne doit pas être
  // bloquée par un compteur indisponible.
  useEffect(() => {
    if (isCreate || isDelete) return;
    const controller = new AbortController();
    fetchFolderImpact(libraryId, target.path, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setImpact(data);
      })
      .catch(() => {
        /* Compteur indisponible : on n'affiche simplement rien. */
      });
    return () => controller.abort();
  }, [isCreate, isDelete, libraryId, target.path]);

  // APERÇU DE SUPPRESSION. Contrairement à l'impact, un échec n'est PAS avalé : ici
  // l'aperçu n'est pas un supplément d'information mais LA condition pour proposer le
  // bouton. Sans lui, on ne sait pas ce qui va disparaître — donc on ne propose rien.
  useEffect(() => {
    if (!isDelete) return;
    const controller = new AbortController();
    fetchFolderDeletePreview(libraryId, target.path, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setPreview(data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setPreview(null);
      });
    return () => controller.abort();
  }, [isDelete, libraryId, target.path]);

  // Arbre PARTAGÉ des dossiers (audit A9) — seulement consulté en mode déplacement.
  const folders = useAllFolders(libraryId);
  const tree: FolderNode[] = useMemo(() => {
    if (!isMove || folders.status !== 'ready') return [];
    // On RETIRE le dossier lui-même et tout son sous-arbre : le proposer comme
    // destination reviendrait à offrir un geste que le serveur refuse (un dossier ne se
    // déplace pas dans lui-même). Mieux vaut ne pas le montrer que le refuser après coup.
    return buildFolderTree(folders.entries).filter(
      (node) => !isSelfOrDescendantFolder(node.path, target.path),
    );
  }, [isMove, folders, target.path]);

  const pickerLabels: FolderTreePickerLabels = useMemo(
    () => ({
      search: t('organize.searchPlaceholder'),
      loading: t('organize.loadingFolders'),
      error: t('organize.foldersError'),
      root: t('folders.root'),
      selectFolder: t('organize.selectFolder'),
      folderCount: t('organize.folderCount'),
      expand: t('organize.expandFolder'),
      collapse: t('organize.collapseFolder'),
      searchEmpty: t('organize.searchEmpty'),
    }),
    [t],
  );

  // Validité CLIENT, miroir exact de la règle serveur (`sanitizeFolderName`).
  const normalizedName = sanitizeFolderName(name);
  const nameEmpty = name.trim() === '';
  const currentName = isCreate ? null : folderNameOf(target.path);
  const currentParent = isCreate ? null : parentFolderPath(target.path);

  // Chemin qui RÉSULTERAIT de l'action — l'aperçu vivant sous le champ.
  const previewPath = useMemo(() => {
    if (isMove) {
      const base = folderNameOf(target.path);
      return destParent === '' ? base : `${destParent}/${base}`;
    }
    if (normalizedName === null) return null;
    const parent = isCreate ? target.path : (currentParent ?? '');
    return parent === '' ? normalizedName : `${parent}/${normalizedName}`;
  }, [isMove, isCreate, destParent, normalizedName, target.path, currentParent]);

  // --- SUPPRESSION : ce qui autorise le bouton ------------------------------
  // Trois conditions CUMULATIVES, chacune pour une raison différente :
  //  1. l'aperçu a répondu et ne signale AUCUN motif de refus (sinon le serveur
  //     refuserait — proposer un geste pour le refuser ensuite est un défaut d'UI) ;
  //  2. s'il reste des fichiers système, l'opt-in est coché (c'est le seul `unlink` du
  //     produit, il ne s'obtient jamais par défaut) ;
  //  3. le mot de confirmation est recopié — la suppression est IRRÉVERSIBLE.
  const deleteConfirmWord = t('folderOps.deleteConfirmWord');
  const deletable = preview != null && preview.blockers.length === 0;
  const systemOptInSatisfied = preview != null && (preview.systemFiles === 0 || removeSystemFiles);
  const canDelete =
    deletable && systemOptInSatisfied && confirmWordMatches(typed, deleteConfirmWord);

  // Le bouton de validation reste inerte tant que l'action ne changerait rien : un
  // renommage vers le même nom ou un déplacement vers le parent actuel sont des
  // non-gestes, que le serveur refuserait pour « destination occupée » — message
  // exact mais déroutant.
  const unchanged = isMove
    ? destParent === currentParent
    : !isCreate && normalizedName === currentName;
  const canSubmit = isDelete
    ? canDelete
    : isMove
      ? !unchanged
      : normalizedName !== null && !unchanged;

  const onSubmit = useCallback(async () => {
    if (busy || !canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const result = isDelete
        ? await deleteFolder(libraryId, { path: target.path, removeSystemFiles })
        : isCreate
          ? await createFolder(libraryId, { parent: target.path, name: name.trim() })
          : isMove
            ? await moveFolder(libraryId, { path: target.path, destParent })
            : await renameFolder(libraryId, { path: target.path, name: name.trim() });
      onDone(result, target.kind);
      onClose();
    } catch (err) {
      setError(classifyError(err));
    } finally {
      setBusy(false);
    }
  }, [
    busy,
    canSubmit,
    isCreate,
    isDelete,
    isMove,
    libraryId,
    name,
    destParent,
    removeSystemFiles,
    target,
    onDone,
    onClose,
  ]);

  const title = t(
    isCreate
      ? 'folderOps.createTitle'
      : isMove
        ? 'folderOps.moveTitle'
        : isDelete
          ? 'folderOps.deleteTitle'
          : 'folderOps.renameTitle',
  );
  const TitleIcon = isCreate
    ? FolderPlusIcon
    : isMove
      ? FolderArrowDownIcon
      : isDelete
        ? TrashIcon
        : PencilSquareIcon;
  const hostLabel = target.path === '' ? t('folders.root') : target.path;

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        {/* En-tête. */}
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            {/* Teinte « danger » pour la suppression — mais l'information est portée par
                l'ICÔNE (corbeille) et par le TITRE, jamais par la couleur seule. */}
            <TitleIcon
              className={`h-6 w-6 ${isDelete ? 'text-danger' : 'text-brand'}`}
              aria-hidden="true"
            />
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            disabled={busy}
            title={t('organize.close')}
            aria-label={t('organize.close')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Corps. */}
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-4">
          <p className="text-sm text-muted">
            {interpolate(
              t(
                isCreate
                  ? 'folderOps.createIntro'
                  : isMove
                    ? 'folderOps.moveIntro'
                    : isDelete
                      ? 'folderOps.deleteIntro'
                      : 'folderOps.renameIntro',
              ),
              { folder: hostLabel },
            )}
          </p>

          {isDelete ? (
            <DeleteBody
              preview={preview}
              busy={busy}
              removeSystemFiles={removeSystemFiles}
              onToggleSystemFiles={setRemoveSystemFiles}
              confirmWord={deleteConfirmWord}
              typed={typed}
              onTypedChange={setTyped}
            />
          ) : isMove ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-fg">{t('folderOps.destLabel')}</p>
              <FolderTreePicker
                status={folders.status}
                nodes={tree}
                selected={destParent}
                onSelect={setDestParent}
                busy={busy}
                enlarged={false}
                labels={pickerLabels}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <label htmlFor="folder-op-name" className="text-sm font-medium text-fg">
                {t('folderOps.nameLabel')}
              </label>
              <input
                id="folder-op-name"
                ref={nameInputRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder={t('folderOps.namePlaceholder')}
                aria-invalid={!nameEmpty && normalizedName === null}
                aria-describedby="folder-op-name-hint"
                className="h-11 rounded-md border border-border bg-surface px-3 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              />
              {nameEmpty ? (
                <span id="folder-op-name-hint" className="text-xs text-muted">
                  {t('folderOps.nameHint')}
                </span>
              ) : normalizedName === null ? (
                <p
                  id="folder-op-name-hint"
                  className="inline-flex items-center gap-1 text-sm text-danger"
                >
                  <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {t('folderOps.nameInvalid')}
                </p>
              ) : (
                <span id="folder-op-name-hint" className="text-xs text-muted">
                  {interpolate(t('folderOps.preview'), { path: previewPath ?? '' })}
                </span>
              )}
            </div>
          )}

          {isMove && previewPath !== null && (
            <p className="rounded-md bg-surface-2 p-3 text-sm text-fg">
              {interpolate(t('folderOps.preview'), { path: previewPath })}
            </p>
          )}

          {/* CE QUI SUIT TOUT SEUL — la propagation, dite explicitement : sans elle, un
              renommage aurait l'air d'un geste anodin alors qu'il recale des centaines de
              lignes. `role="status"` : c'est une information, pas une alerte.
              Trois comptes PLURALISÉS séparément (`tp`) : une seule clé pour trois
              nombres ne saurait pas s'accorder, en français comme en allemand. */}
          {!isCreate && impact !== null && (
            <p role="status" className="rounded-md bg-surface-2 p-3 text-sm text-fg">
              {t('folderOps.impactIntro')}{' '}
              {[
                tp('folderOps.impactAssets', impact.assets),
                tp('folderOps.impactShares', impact.shares),
                tp('folderOps.impactGrants', impact.grants),
              ].join(' · ')}
            </p>
          )}

          {/* CE QUI NE SUIT PAS — l'avertissement. Icône + texte, jamais la couleur seule. */}
          {!isCreate && impact !== null && impact.savedFilters > 0 && (
            <p
              role="status"
              className="inline-flex items-start gap-2 rounded-md border border-warning bg-warning/10 p-3 text-sm text-fg"
            >
              <ExclamationTriangleIcon
                className="mt-0.5 h-5 w-5 shrink-0 text-warning-text"
                aria-hidden="true"
              />
              <span>{tp('folderOps.savedFiltersWarning', impact.savedFilters)}</span>
            </p>
          )}

          {error !== null && (
            <p role="alert" className="inline-flex items-start gap-2 text-sm text-danger">
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>{t(ERROR_MESSAGE_KEY[error])}</span>
            </p>
          )}
        </div>

        {/* Actions. */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            {t('organize.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void onSubmit()}
            disabled={!canSubmit || busy}
            // Bouton « danger » pour la suppression — redondant avec l'icône de corbeille
            // et le libellé « Supprimer », qui portent seuls le sens (WCAG AA).
            className={
              isDelete
                ? 'inline-flex items-center gap-2 rounded-md bg-danger px-4 py-2 text-sm font-medium text-danger-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:cursor-not-allowed disabled:opacity-50'
                : 'inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50'
            }
          >
            {busy ? (
              <ArrowPathIcon
                className="h-5 w-5 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
            ) : (
              isDelete && <TrashIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            )}
            {t(
              isCreate
                ? 'folderOps.createSubmit'
                : isMove
                  ? 'folderOps.moveSubmit'
                  : isDelete
                    ? 'folderOps.deleteSubmit'
                    : 'folderOps.renameSubmit',
            )}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

/**
 * CORPS DE LA CONFIRMATION DE SUPPRESSION — ce qui va se passer, dit AVANT de le faire.
 *
 * Trois blocs, dans l'ordre où on les lit :
 *  1. la GARANTIE (`rmdir` seul, aucun fichier effacé sans l'option) — c'est elle qui
 *     rend le geste acceptable, elle vient donc en premier ;
 *  2. l'APERÇU : soit les motifs de REFUS (avec la conduite à tenir), soit le nombre de
 *     répertoires qui partiront et les fichiers système qui seraient effacés, NOMMÉS ;
 *  3. la CONFIRMATION ACTIVE : recopier un mot, comme la réinitialisation de
 *     bibliothèque (`components/confirm-word.ts` porte la règle et sa raison).
 *
 * L'aperçu est PÉRISSABLE et ne décide de rien : le serveur re-calcule tout au moment
 * d'agir. Il sert à ce que l'utilisateur sache, pas à raccourcir le travail du serveur.
 */
function DeleteBody({
  preview,
  busy,
  removeSystemFiles,
  onToggleSystemFiles,
  confirmWord,
  typed,
  onTypedChange,
}: {
  /** `undefined` = en cours de chargement, `null` = aperçu indisponible. */
  preview: FolderDeletePreview | null | undefined;
  busy: boolean;
  removeSystemFiles: boolean;
  onToggleSystemFiles: (value: boolean) => void;
  confirmWord: string;
  typed: string;
  onTypedChange: (value: string) => void;
}): React.JSX.Element {
  const { t, tp, locale } = useI18n();

  if (preview === undefined) {
    return (
      <p role="status" className="inline-flex items-center gap-2 text-sm text-muted">
        <ArrowPathIcon
          className="h-5 w-5 animate-spin motion-reduce:animate-none"
          aria-hidden="true"
        />
        {t('folderOps.deleteScanning')}
      </p>
    );
  }
  if (preview === null) {
    return (
      <p role="alert" className="inline-flex items-start gap-2 text-sm text-danger">
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <span>{t('folderOps.deletePreviewError')}</span>
      </p>
    );
  }

  const blocked = preview.blockers.length > 0;
  return (
    <>
      {/* 1. LA GARANTIE. Information, pas alerte → icône « info » et `text-muted`. */}
      <p className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-muted">
        <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
        {t('folderOps.deleteSafetyNote')}
      </p>

      {/* 2a. LES MOTIFS DE REFUS, un par un, avec la conduite à tenir. Icône + texte,
             jamais la couleur seule. */}
      {blocked && (
        <div
          role="status"
          className="flex flex-col gap-2 rounded-md border border-warning bg-warning/10 p-3 text-sm text-fg"
        >
          <p className="font-medium">{t('folderOps.deleteBlockedTitle')}</p>
          <ul className="flex flex-col gap-1">
            {preview.blockers.map((blocker) => (
              <li key={blocker} className="flex items-start gap-2">
                <ExclamationTriangleIcon
                  className="mt-0.5 h-5 w-5 shrink-0 text-warning-text"
                  aria-hidden="true"
                />
                <span>
                  {blocker === 'indexed_assets'
                    ? tp('folderOps.blockerIndexedAssets', preview.assets)
                    : t(BLOCKER_MESSAGE_KEY[blocker])}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 2b. CE QUI VA DISPARAÎTRE. Le compte inclut le dossier visé lui-même. */}
      {!blocked && (
        <p role="status" className="rounded-md bg-surface-2 p-3 text-sm text-fg">
          {tp('folderOps.deleteFolderCount', preview.folders)}
        </p>
      )}

      {/* 2c. LES FICHIERS SYSTÈME et leur opt-in. Affichés même décochés — savoir qu'ils
             sont là fait partie de la réponse (même règle qu'ADR-0010 amendée). Ils sont
             NOMMÉS : « 3 fichiers seront effacés » sans dire lesquels n'est pas une
             confirmation. */}
      {!blocked && preview.systemFiles > 0 && (
        <div className="flex flex-col gap-2 rounded-md border border-border p-3">
          <p className="text-sm font-medium text-fg">
            {tp('folderOps.deleteSystemCount', preview.systemFiles)}
          </p>
          <p className="text-xs text-muted">
            {interpolate(
              t('folderOps.deleteSystemNames'),
              { names: preview.systemFileNames.join(', ') },
              locale,
            )}
          </p>
          <label className="flex items-start gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={removeSystemFiles}
              onChange={(e) => onToggleSystemFiles(e.target.checked)}
              disabled={busy}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-border-strong accent-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            />
            <span>{t('folderOps.deleteSystemOptIn')}</span>
          </label>
        </div>
      )}

      {/* 3. CONFIRMATION ACTIVE — masquée quand la suppression sera refusée de toute
             façon : demander de recopier un mot pour un bouton inerte serait absurde. */}
      {!blocked && (
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium text-fg">
            {interpolate(t('folderOps.deleteConfirmLabel'), { word: confirmWord })}
          </span>
          <input
            type="text"
            value={typed}
            onChange={(e) => onTypedChange(e.target.value)}
            disabled={busy}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder={confirmWord}
            className="h-11 rounded-md border border-border bg-surface px-3 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:opacity-50"
          />
        </label>
      )}
    </>
  );
}
